<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <CardRow v-if="isEffContractVisibility">
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          label="Работа входит в эффективный контракт"
          v-model="contest.commonWork.isEffContract"
          hide-details
          dense
          :disabled="isShowMode && !canChangeIsEffContractInShowMode"
          @change="updateContest"
        />
      </CardRow>

      <h4 class="my-4 mt-10">НИР</h4>

      <CardAutocompleteRow
        v-model="contest.contestBody.nirType"
        :items="nirTypeList"
        item-value="id"
        item-text="name"
        label="Тип НИР"
        placeholder="Выберите тип НИР"
        :disabled="isShowMode"
        :error-messages="errorMessages.nirType"
        :required="!isShowMode"
        @input="
          $v.contest.contestBody.nirType.$touch()
          updateContest()
        "
      />

      <CardFieldRow
        v-model="contest.commonWork.workName"
        label="Название темы НИР"
        placeholder="Добавьте название темы НИР"
        :disabled="isShowMode"
        :error-messages="errorMessages.workName"
        :required="!isShowMode"
        @input="
          $v.contest.commonWork.workName.$touch()
          updateContest()
        "
      />

      <CardFieldRow
        v-model="contest.contestBody.contestName"
        label="Название конкурса"
        placeholder="Добавьте название конкурса"
        :disabled="isShowMode"
        :error-messages="errorMessages.contestName"
        :required="!isShowMode"
        @input="
          $v.contest.contestBody.contestName.$touch()
          updateContest()
        "
      />

      <CardFieldRow
        v-model="contest.contestBody.nirNumber"
        type="number"
        label="Номер договора"
        placeholder="Добавьте номер договора"
        :disabled="isShowMode"
        :error-messages="errorMessages.nirNumber"
        :required="!isShowMode"
        @input="
          $v.contest.contestBody.nirNumber.$touch()
          updateContest()
        "
      />

      <CardDateRow
        v-model="contest.executionFromDate"
        label="Дата начала НИР"
        placeholder="Добавьте дату начала НИР"
        :disabled="isShowMode"
        :error-messages="errorMessages.executionFromDate"
        :required="!isShowMode"
        @input="
          $v.contest.executionFromDate.$touch()
          updateContest()
        "
      />

      <CardDateRow
        v-model="contest.executionToDate"
        label="Дата окончания НИР"
        placeholder="Добавьте дату окончания НИР"
        :disabled="isShowMode"
        :error-messages="errorMessages.executionToDate"
        :required="!isShowMode"
        @input="
          $v.contest.executionToDate.$touch()
          updateContest()
        "
      />

      <h4 class="my-4 mt-10">Финансирование</h4>

      <CardAutocompleteRow
        v-model="contest.contestBody.nirLevel"
        :items="nirLevelList"
        item-value="id"
        item-text="name"
        label="Уровень НИР"
        placeholder="Выберите уровень НИР"
        :disabled="isShowMode"
        :error-messages="errorMessages.nirLevel"
        :required="!isShowMode"
        @input="
          $v.contest.contestBody.nirLevel.$touch()
          updateContest()
        "
      />

      <CardFieldRow
        v-model="contest.contestBody.nirClient"
        label="Заказчик НИР"
        placeholder="Добавьте заказчик НИР"
        :disabled="isShowMode"
        :error-messages="errorMessages.nirClient"
        :required="!isShowMode"
        @input="
          $v.contest.contestBody.nirClient.$touch()
          updateContest()
        "
      />

      <CardFieldRow
        v-model="contest.contestBody.fundingSum"
        type="number"
        label="Объем финансирования на год"
        placeholder="Добавьте объем финансирования на год"
        :disabled="isShowMode"
        :error-messages="errorMessages.fundingSum"
        :required="!isShowMode"
        @input="
          $v.contest.contestBody.fundingSum.$touch()
          updateContest()
        "
      />

      <div
        v-if="!isShowMode"
        class="d-flex"
      >
        <UIAlert title="Необходимо добавить ссылку и/или прикрепить подтверждающий документ" />
      </div>

      <CardFieldRow
        v-model="contest.link"
        label="Ссылка на подтверждающий документ"
        placeholder="Добавьте ссылку на подтверждающий документ"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.link"
        :required="!isShowMode"
        @input="
          $v.contest.link.$touch()
          updateContest()
        "
      />

      <CardRow
        label="Подтверждающие материалы"
        class="mt-0"
      >
        <UIUploadFile
          button-text="Добавить подтверждающие материалы"
          acceptFileFormats=".pdf, .jpeg, .jpg, .doc, .docx"
          :maxFileWeightInMb="5"
          :is-show-mode="isShowMode"
          :multiple="true"
          :maxFiles="10"
          :fileList="linkFileList"
          @uploadFiles="uploadFiles"
          @removeFile="removeFile"
        />
      </CardRow>

      <CardAutocompleteRow
        v-model="contest.contestResult"
        :items="contestResultList"
        item-value="id"
        item-text="name"
        label="Результат"
        placeholder="Выберите результат"
        :disabled="isShowMode"
        :error-messages="errorMessages.contestResult"
        :required="!isShowMode"
        @input="
          $v.contest.contestResult.$touch()
          resetCommitmentDate(), updateContest()
        "
      />

      <CardDateRow
        v-if="commitmentDateVisibility"
        v-model="contest.commitmentDate"
        label="Дата исполнения обязательств"
        placeholder="Добавьте дату исполнения обязательств"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.commitmentDate"
        @input="$v.contest.commitmentDate.$touch(), updateContest()"
      />

      <h4 class="my-4 mt-10">Совместно с какими научными подразделениями Университета выполняется НИР</h4>

      <CardAutocompleteRow
        v-model="contest.commonWork.scientDepartments"
        :items="scientDepartmentList"
        item-value="id"
        item-text="name"
        label="Подразделения"
        placeholder="Выберите подразделения"
        multiple
        hide-border-bottom
        :disabled="isShowMode"
        :no-filter="false"
        @input="updateContest()"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import CardRow from '@/components/ui/row/CardRow'
import { Contest } from '@/models'
import CardFieldRow from '../../ui/row/CardFieldRow'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import { setValidateMessages } from '../../../scripts/validation'
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
import { checkDate, compareTwoDates } from '@/scripts/validation'
import CardDateRow from '../../ui/row/CardDateRow'
import { mapGetters } from 'vuex'
import UIUploadFile from '@/components/ui/UIUploadFile.vue'
import UIAlert from '@/components/ui/UIAlert.vue'
import { isEffContractMixin } from '@/mixins/isEffContract'

export default {
  components: { UIAlert, UIUploadFile, CardDateRow, CardAutocompleteRow, CardFieldRow, CardRow },

  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    title: String,
    linkFileList: Array
  },

  mixins: [isEffContractMixin],

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    contest: new Contest(),

    contestResultId: {
      prizePlace: 1,
      bonus: 2,
      agreement: 3
    }
  }),

  validations() {
    return {
      contest: {
        commonWork: {
          workName: { required, maxLength: maxLength(1500) },
          recipientDepartment: {}
        },

        contestBody: {
          nirType: { required },
          contestName: { required, maxLength: maxLength(300) },
          nirNumber: { required, maxLength: maxLength(10) },
          nirLevel: { required },
          nirClient: { required, maxLength: maxLength(300) },
          fundingSum: { required, maxLength: maxLength(12) }
        },

        executionFromDate: { required, checkDate },
        executionToDate: {
          required,
          checkDate,
          compareTwoDates
        },
        link: { required: requiredIf(() => !this.linkFileList.length) },
        contestResult: { required },
        commitmentDate: {
          required: requiredIf(() => this.commitmentDateVisibility)
        }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['nirTypeList', 'scientDepartmentList', 'nirLevelList', 'contestResultList']),
    ...mapGetters('user', ['isAdmin', 'isModerator', 'isModeratorNir']),

    currentWork() {
      return this.contest
    },

    commitmentDateVisibility() {
      return [this.contestResultId.bonus, this.contestResultId.agreement].includes(this.contest.contestResult?.id)
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.contest.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.contest[key])
      })

      Object.keys(this.$v.contest.contestBody.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.contest.contestBody[key])
      })

      Object.keys(this.$v.contest.commonWork.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.contest.commonWork[key])
      })

      return allErrors
    }
  },

  methods: {
    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    async updateContest() {
      this.$emit('update:selected-item', new Contest(this.contest.getForAPI()))
      this.updateInvalid()
    },

    resetCommitmentDate() {
      this.contest.commitmentDate = null
    },

    uploadFiles(files) {
      this.$emit('uploadLinkFiles', files)
      this.updateContest()
    },

    removeFile(index) {
      this.$emit('removeLinkFile', index)
      this.updateContest()
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.contest = new Contest(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
